import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Redirect to login if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API interfaces
export interface QueryResult {
  columns: string[];
  rows: any[][];
  row_count: number;
  execution_time: number;
}

export interface TableInfo {
  name: string;
  schema: string;
  row_count?: number;
}

export interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default?: string;
}

export interface DatabaseStats {
  table_stats: {
    schemaname: string;
    tablename: string;
    inserts: number;
    updates: number;
    deletes: number;
    live_tuples: number;
    dead_tuples: number;
  }[];
  timestamp: string;
}

export interface RecordCreate {
  table: string;
  schema: string;
  data: Record<string, any>;
}

export interface RecordUpdate {
  table: string;
  schema: string;
  record_id: number | string;
  id_column?: string;  // defaults to "id"
  data: Record<string, any>;
}

export interface RecordDelete {
  table: string;
  schema: string;
  record_id: number | string;
  id_column?: string;  // defaults to "id"
}

export interface SearchRequest {
  table: string;
  schema: string;
  search_column: string;
  search_value: string;
  limit: number;
}

// API functions
export const executeQuery = async (sql: string): Promise<QueryResult> => {
  const response = await api.post('/api/query', { sql });
  return response.data;
};

export const getTables = async (): Promise<TableInfo[]> => {
  const response = await api.get('/api/tables');
  return response.data;
};

export const getTableColumns = async (schema: string, table: string): Promise<ColumnInfo[]> => {
  const response = await api.get(`/api/tables/${schema}/${table}/columns`);
  return response.data;
};

export const getTablePrimaryKeys = async (schema: string, table: string): Promise<string[]> => {
  const response = await api.get(`/api/tables/${schema}/${table}/primary-keys`);
  return response.data;
};

export const getTableData = async (
  schema: string, 
  table: string, 
  limit: number = 100, 
  offset: number = 0
) => {
  const response = await api.get(`/api/tables/${schema}/${table}/data`, {
    params: { limit, offset }
  });
  return response.data;
};

export const getDatabaseStats = async (): Promise<DatabaseStats> => {
  const response = await api.get('/api/database/stats');
  return response.data;
};

export const checkHealth = async () => {
  const response = await api.get('/health');
  return response.data;
};

// CRUD Operations
export const createRecord = async (record: RecordCreate) => {
  const response = await api.post('/api/records', record);
  return response.data;
};

export const updateRecord = async (record: RecordUpdate) => {
  const response = await api.put('/api/records', record);
  return response.data;
};

export const deleteRecord = async (record: RecordDelete) => {
  const response = await api.delete('/api/records', { data: record });
  return response.data;
};

export const searchRecords = async (search: SearchRequest) => {
  const response = await api.post('/api/search', search);
  return response.data;
};

export default api;